import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { ConfigConstants, isDefined } from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, Subject } from 'rxjs';
import {
    debounceTime,
    distinctUntilChanged,
    filter,
    map,
    switchMap,
    take,
    takeUntil,
    withLatestFrom,
} from 'rxjs/operators';
import {
    updateFunctionalElementRemarks,
    updateFunctionalElementSelection,
} from '../../store/actions/functional-elements.actions';
import {
    getFormFilters,
    getFormFunctionalElementByIdFactory,
    getFormParticipantCopyReadonly,
    getFormSelectedParticipantCopyId,
    getFormTypeFilters,
    getOptionPresentNoSelectedForFunctionalElement,
    isCosimaFunctionalElementFromTrainingChecklistReadOnly,
} from '../../store/selectors/form.selector';
import { FormsState } from '../../store/state/pace-forms.state';
import { Choice, CHOICE_TYPE, Choices } from '../../types/choice';
import { ContentId } from '../../types/content-block';
import { FormFilters } from '../../types/form-filter';
import { FormTypeFilters } from '../../types/form-type-filters';
import { FormsConstants } from '../../types/forms-constants';
import { FUNCTIONAL_ELEMENT_TYPE } from '../../types/functional-element';
import { ParticipantCopyId } from '../../types/participant-copy';
import { StandardFunctionalElement } from '../../types/standard-functional-element';
import { isCosimaFunctionalElementVisible } from '../../utilities/answer-utilities';
import { FormUtilities } from '../../utilities/form-utilities';

@Component({
    selector: 'pace-forms-standard-functional-element',
    templateUrl: './standard-functional-element.component.html',
    styleUrls: ['./standard-functional-element.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StandardFunctionalElementComponent implements OnInit, OnDestroy {
    @Input()
    public id: ContentId;

    @Input()
    public parentForm: FormArray | undefined;

    public visible$: Observable<boolean>;
    public readOnly$: Observable<boolean>;
    public standardFunctionalElement$: Observable<StandardFunctionalElement>;
    public choices$: Observable<Choices>;
    public selectedChoice$: Observable<Choice | undefined>;
    public negativeChoiceSelected$: Observable<boolean>;
    public typeFilters$: Observable<FormTypeFilters>;
    public formFilters$: Observable<FormFilters>;
    public elementTypeLabel$: Observable<string>;
    public selectedParticipant$: Observable<ParticipantCopyId>;
    public showRemarksField$: Observable<boolean>;
    public optionPresentNoSelected$: Observable<boolean>;

    private destroyTrigger$: Subject<void>;

    public standardFunctionalElementGroup: FormGroup;
    public radioGroup: FormGroup;
    public radioControl: FormControl;
    public remarksControl: FormControl;
    protected readonly FormsConstants = FormsConstants;
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly formBuilder: FormBuilder,
        private readonly store: Store<FormsState>,
        public readonly cdr: ChangeDetectorRef,
    ) {
        this.readOnly$ = store.select(getFormParticipantCopyReadonly);
        this.typeFilters$ = store.select(getFormTypeFilters);
        this.formFilters$ = store.select(getFormFilters);
        this.selectedParticipant$ = store.select(getFormSelectedParticipantCopyId);
        this.destroyTrigger$ = new Subject<void>();
    }

    public ngOnInit(): void {
        this.radioControl = this.formBuilder.control('');
        this.remarksControl = this.formBuilder.control('');
        this.radioGroup = this.formBuilder.group({ radioControl: this.radioControl });
        this.standardFunctionalElementGroup = this.formBuilder.group({
            remarksControl: this.remarksControl,
            radioGroup: this.radioGroup,
        });

        if (this.parentForm) {
            const parent = this.parentForm;
            Promise.resolve(undefined)
                .then(() => {
                    parent.push(this.standardFunctionalElementGroup);
                })
                .catch((error: Error) => {
                    this.log.error(
                        'StandardFunctionalElementComponent: ngOnInit: Error in parentForm.push: ',
                        JSON.stringify(error),
                    );
                });
        }

        const optionPresentNoSelectedSelector = getOptionPresentNoSelectedForFunctionalElement({ contentId: this.id });
        this.optionPresentNoSelected$ = this.store.select(optionPresentNoSelectedSelector);

        this.standardFunctionalElement$ = this.store
            .select(getFormFunctionalElementByIdFactory<StandardFunctionalElement>({ id: this.id }))
            .pipe(filter(isDefined));

        this.readOnly$ = combineLatest([
            this.store.select(getFormParticipantCopyReadonly),
            this.store.select(isCosimaFunctionalElementFromTrainingChecklistReadOnly({ id: this.id })),
        ]).pipe(map(([readOnly, isCosimaReadOnly]) => readOnly || isCosimaReadOnly));

        this.elementTypeLabel$ = combineLatest([
            this.standardFunctionalElement$.pipe(
                map((s) => s.elementType),
                filter(isDefined),
            ),
            this.store.select(getFormTypeFilters),
        ]).pipe(map(([elementType, filters]) => FormUtilities.getFilterLabel(elementType, filters)));

        this.showRemarksField$ = this.standardFunctionalElement$.pipe(
            map(
                (s) =>
                    !!(s.remarks?.length || s.choices?.choiceNegative?.selected) ||
                    s.type === FUNCTIONAL_ELEMENT_TYPE.REMARK,
            ),
        );

        this.choices$ = this.standardFunctionalElement$.pipe(
            map((s) => s.choices),
            filter(isDefined),
        );

        this.selectedChoice$ = this.choices$.pipe(
            map((choices) =>
                Object.values(choices)
                    .filter(isDefined)
                    .find((c) => c.selected),
            ),
        );

        this.negativeChoiceSelected$ = this.selectedChoice$.pipe(map((c) => c?.type === CHOICE_TYPE.CHOICE_NEGATIVE));

        this.visible$ = combineLatest([
            this.standardFunctionalElement$,
            this.choices$,
            this.selectedChoice$,
            this.typeFilters$,
            this.formFilters$,
            this.selectedParticipant$,
        ]).pipe(
            map(([standardFunctionalElement, choices, , typeFilters, formFilters, selectedParticipant]) => {
                const choicesValues = Object.values(choices);

                // standard elements without choices do not make sense, hence we will not display them.
                if (choicesValues.length === 0 && standardFunctionalElement.type === FUNCTIONAL_ELEMENT_TYPE.STANDARD) {
                    return false;
                }

                const isMasterCopy = selectedParticipant === FormsConstants.MASTER_PARTICIPANT_ID;

                // in participant copies, only content that was answered with "Yes" in this session is displayed
                const selectedInMaster = choicesValues.filter(isDefined).find((c) => c.selectedInMaster);
                const yesInMaster =
                    typeof selectedInMaster !== 'undefined' && selectedInMaster.type === CHOICE_TYPE.CHOICE_POSITIVE;
                const visibleInParticipantCopy = !standardFunctionalElement.contentLoadedFromXml && yesInMaster;

                // filters apply both to master and participant copies
                const visibleByFilter = isCosimaFunctionalElementVisible(
                    standardFunctionalElement,
                    typeFilters,
                    formFilters,
                    standardFunctionalElement.elementType ?? FormsConstants.ELEMENT_TYPE.UNKNOWN,
                );

                const visible = (isMasterCopy || visibleInParticipantCopy) && visibleByFilter;

                return visible;
            }),
        );

        combineLatest([
            this.standardFunctionalElement$.pipe(distinctUntilChanged()),
            this.readOnly$.pipe(distinctUntilChanged()),
        ])
            .pipe(
                switchMap(([standardFunctionalElement, readOnly]) =>
                    combineLatest([
                        this.selectedChoice$.pipe(distinctUntilChanged()),
                        this.optionPresentNoSelected$.pipe(distinctUntilChanged()),
                        this.visible$.pipe(distinctUntilChanged()),
                    ]).pipe(
                        map(([selectedChoice, optionPresentNoSelected, visible]) => ({
                            standardFunctionalElement,
                            readOnly,
                            selectedChoice,
                            optionPresentNoSelected,
                            visible,
                        })),
                    ),
                ),
                takeUntil(this.destroyTrigger$),
            )
            .subscribe(({ standardFunctionalElement, readOnly, selectedChoice, optionPresentNoSelected, visible }) => {
                if (!visible) {
                    return;
                }

                const remarksFunctionalElementHasEnteredRemarks =
                    standardFunctionalElement.type === FUNCTIONAL_ELEMENT_TYPE.REMARK &&
                    standardFunctionalElement.remarks &&
                    standardFunctionalElement.remarks.length > 0;

                const emitEvent = FormUtilities.shouldEmitEvent(standardFunctionalElement.choices, selectedChoice);

                if (isDefined(selectedChoice)) {
                    this.radioControl.patchValue(selectedChoice.xmlId);
                } else if (!remarksFunctionalElementHasEnteredRemarks) {
                    this.radioControl.reset('', { emitEvent });
                }
                this.radioControl[optionPresentNoSelected || readOnly ? 'disable' : 'enable']({
                    emitEvent: false,
                });

                this.remarksControl[
                    !readOnly &&
                    (selectedChoice?.type === CHOICE_TYPE.CHOICE_NEGATIVE ||
                        (standardFunctionalElement.type === FUNCTIONAL_ELEMENT_TYPE.REMARK &&
                            selectedChoice?.type !== CHOICE_TYPE.CHOICE_NA))
                        ? 'enable'
                        : 'disable'
                ]({
                    emitEvent: false,
                });
                this.remarksControl.patchValue(standardFunctionalElement.remarks);

                this.cdr.detectChanges();
            });

        this.radioControl.valueChanges
            .pipe(
                distinctUntilChanged(),
                withLatestFrom(this.standardFunctionalElement$.pipe(take(1))),
                takeUntil(this.destroyTrigger$),
            )
            .subscribe(([value, standardFunctionalElement]: [string | null, StandardFunctionalElement]) => {
                this.store.dispatch(
                    updateFunctionalElementSelection({
                        id: standardFunctionalElement.id,

                        selectedChoiceXmlId: value === null ? undefined : value,
                    }),
                );
            });

        this.remarksControl.valueChanges
            .pipe(
                debounceTime(ConfigConstants.debounceTimeMs),
                distinctUntilChanged(),
                withLatestFrom(this.standardFunctionalElement$.pipe(take(1))),
                takeUntil(this.destroyTrigger$),
            )
            .subscribe(([newValue, standardFunctionalElement]: [string, StandardFunctionalElement]) => {
                if (!isDefined(standardFunctionalElement.xmlId)) {
                    throw new Error(
                        `StandardFunctionalElementComponent: remarksControl valueChanges: Functional element has no xml Id!`,
                    );
                }

                if (standardFunctionalElement.remarksXmlId) {
                    this.store.dispatch(
                        updateFunctionalElementRemarks({
                            id: standardFunctionalElement.id,
                            // using ! because remarksXmlId is guaranteed to be there in Cosima. It is only undefined in Astoria.
                            xmlId: standardFunctionalElement.remarksXmlId,
                            newValue,
                        }),
                    );
                }
            });
    }

    public ngOnDestroy(): void {
        if (this.parentForm) {
            const index = this.parentForm.controls.findIndex((control) => control === this.radioGroup);
            this.parentForm.removeAt(index);
        }

        this.destroyTrigger$.next();
    }

    public reset(): void {
        this.radioControl.reset();
    }
}
